import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { Observable, from, of, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { environment } from '@app-environment';
import { RootServicesModule } from './root-services.module';

@Injectable({
    providedIn: RootServicesModule
})
export class GraphService {
    private http = inject(HttpClient);
    private msalService = inject(MsalService);

    /**
     * Get a specific user's profile photo by ID or UPN.
     * @param userIdentifier User ID, UPN, or 'me' for current user
     * @returns Observable with the photo as a Blob
     */
    public getPhotoForUser(userIdentifier: string): Observable<Blob> {
        const scopes = [environment.apiConfig[3].scopes[0], environment.apiConfig[3].scopes[1]];
        return this.getGraphToken(scopes).pipe(
            switchMap((token) => {
                const headers = new HttpHeaders({
                    Authorization: `Bearer ${token}`
                });

                return this.http.get(`${environment.apiConfig[3].uri}/users/${userIdentifier}/photo/$value`, {
                    headers,
                    responseType: 'blob'
                });
            }),
            catchError((error) => {
                console.error('Error fetching user photo:', error);
                return throwError(() => new Error('Failed to fetch user photo'));
            })
        );
    }

    /**
     * Get a token specifically for Microsoft Graph API.
     * @returns Observable with the access token
     */
    private getGraphToken(scopes: string[]): Observable<string> {
        const account = this.msalService.instance.getActiveAccount();
        if (!account) {
            return throwError(() => new Error('No active account! Please sign in before trying again.'));
        }

        const request = {
            scopes,
            account
        };

        return from(this.msalService.instance.acquireTokenSilent(request)).pipe(
            switchMap((response) => of(response.accessToken)),
            catchError((error) => {
                console.error('Silent token acquisition failed', error);
                return from(this.msalService.instance.acquireTokenPopup(request)).pipe(
                    switchMap((response) => of(response.accessToken)),
                    catchError((interactiveError) => {
                        console.error('Interactive token acquisition failed', interactiveError);
                        return throwError(() => new Error('Failed to acquire token'));
                    })
                );
            })
        );
    }
}
